/**
 * Type definitions for Speed Compliance Report
 */

export interface Organization {
  id: string;
  name: string;
  children?: Organization[];
  depth?: number;
}

export interface SpeedComplianceStats {
  organizationId: string;
  organizationName: string;
  totalTrips: number;
  totalDistance: number;
  totalPoints: number;
  totalOverspeeds: number;
  overspeedDuration: number;
  avgSpeed: number;
  maxSpeed: number;
  complianceRate: number;
  overspeedsPerTrip: number;
  overspeedsPerKm: number;
}

export interface VehicleSpeedStats {
  vehicleId: string;
  registrationNumber: string;
  make: string;
  model: string;
  organizationName: string;
  totalTrips: number;
  totalDistance: number;
  totalPoints: number;
  totalOverspeeds: number;
  overspeedDuration: number;
  avgSpeed: number;
  maxSpeed: number;
  complianceRate: number;
}

export interface Summary {
  totalOrganizations: number;
  totalTrips: number;
  totalDistance: number;
  totalOverspeeds: number;
  avgComplianceRate: number;
  overspeedDuration: number;
}

export interface TrendData {
  period: string;
  totalTrips: number;
  totalDistance: number;
  totalOverspeeds: number;
  complianceRate: number;
}

export interface SpeedSegment {
  speedLimit: number;
  totalPoints: number;
  overspeedPoints: number;
  violationPct: number;
  maxSpeed: number;
  maxExcess: number;
}

export type TabType = 'overview' | 'organizations' | 'vehicles' | 'trend' | 'segments';

// Helper functions
export const getViolationColor = (violationRate: number): string => {
  if (violationRate <= 5) return 'text-green-600 bg-green-100 dark:bg-green-900/30';
  if (violationRate <= 15) return 'text-lime-600 bg-lime-100 dark:bg-lime-900/30';
  if (violationRate <= 30) return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30';
  if (violationRate <= 50) return 'text-orange-600 bg-orange-100 dark:bg-orange-900/30';
  return 'text-red-600 bg-red-100 dark:bg-red-900/30';
};

export const getViolationRate = (complianceRate: number): number => {
  const violation = 100 - complianceRate;
  if (violation < 1 && violation > 0) {
    return Math.round(violation * 100) / 100;
  }
  return Math.round(violation * 10) / 10;
};

export const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

export const findOrgInTree = (orgs: Organization[], id: string): Organization | null => {
  for (const org of orgs) {
    if (org.id === id) return org;
    if (org.children) {
      const found = findOrgInTree(org.children, id);
      if (found) return found;
    }
  }
  return null;
};
